
'use client';

import { useState, useMemo } from 'react';
import Link from 'next/link';
import { Search, Car, Bike, Truck, Grid } from 'lucide-react';
import type { Brand } from '@/lib/brands';

interface BrandGridProps {
    brands: Brand[];
}

export default function BrandGrid({ brands }: BrandGridProps) {
    const [search, setSearch] = useState('');
    const [filter, setFilter] = useState<Brand['type'] | 'all'>('all');

    const filteredBrands = useMemo(() => {
        return brands.filter(brand => {
            const matchesSearch = brand.name.toLowerCase().includes(search.toLowerCase());
            const matchesFilter = filter === 'all' || brand.type === filter;
            // Exclude "other" from default view unless searched? Nah, just show legal types.
            const isRelevant = brand.type !== 'other'; // Optionally hide "Auto Shows" unless explicitly asked?
            // Actually user asked for "Car Brands", let's be inclusive but filtering helps.

            return matchesSearch && matchesFilter && (filter === 'all' ? isRelevant : true);
        });
    }, [brands, search, filter]);

    const tabs = [
        { id: 'all', label: 'All Brands', icon: Grid },
        { id: 'car', label: 'Cars', icon: Car },
        { id: 'motorcycle', label: 'Motorcycles', icon: Bike },
        { id: 'truck', label: 'Trucks', icon: Truck },
    ] as const;

    return (
        <div className="space-y-8">
            {/* Controls */}
            <div className="flex flex-col md:flex-row gap-4 justify-between items-center bg-[var(--bg-elevated)] p-4 rounded-xl border border-[var(--color-border)] shadow-sm">

                {/* Search */}
                <div className="relative w-full md:w-96">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-[var(--color-text-muted)]">
                        <Search size={18} />
                    </div>
                    <label htmlFor="brand-search" className="sr-only">Search brands</label>
                    <input
                        id="brand-search"
                        name="q"
                        type="text"
                        placeholder="Search brands..."
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        className="w-full pl-10 pr-4 py-2 bg-[var(--bg-secondary)] border border-[var(--color-border)] rounded-lg focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)] focus:border-transparent transition-all"
                    />
                </div>

                {/* Tabs */}
                <div className="flex gap-2 p-1 bg-[var(--bg-secondary)] rounded-lg overflow-x-auto max-w-full">
                    {tabs.map((tab) => {
                        const Icon = tab.icon;
                        const isActive = filter === tab.id;
                        return (
                            <button
                                key={tab.id}
                                onClick={() => setFilter(tab.id as any)}
                                className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-all whitespace-nowrap
                                    ${isActive
                                        ? 'bg-[var(--bg-elevated)] text-[var(--color-primary)] shadow-sm'
                                        : 'text-[var(--color-text-muted)] hover:text-[var(--color-text-main)] hover:bg-[var(--bg-elevated)]/50'
                                    }`}
                            >
                                <Icon size={16} />
                                {tab.label}
                            </button>
                        );
                    })}
                </div>
            </div>

            {/* Grid */}
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-6">
                {filteredBrands.map((brand) => (
                    <Link
                        key={brand.slug}
                        href={`/category/${brand.slug.replace(/\.(jpg|png)$/, '')}`}
                        // Note: brand.slug in our lib is filename e.g. "audi.jpg". We need to strip ext IF it somehow got in, 
                        // but logic says lib stores filename "audi". Wait, lib code: `const fileName = path.parse(file).name;`
                        // So slug is "audi". Safe.
                        // Wait, check lib again. `name: fileName`. Yes.
                        className="group flex flex-col items-center bg-[var(--bg-elevated)] border border-[var(--color-border)] rounded-xl p-6 transition-all duration-300 hover:border-[var(--color-primary)] hover:shadow-lg hover:shadow-[var(--color-primary)]/10 hover:-translate-y-1"
                    >
                        <div className="w-full aspect-[3/2] flex items-center justify-center mb-4 p-4 bg-white/5 rounded-lg overflow-hidden relative">
                            {/* Logo */}
                            <img
                                src={brand.logoUrl}
                                alt={`${brand.name} logo`}
                                className="max-w-full max-h-full object-contain filter grayscale group-hover:grayscale-0 transition-all duration-500 opacity-80 group-hover:opacity-100"
                                loading="lazy"
                            />
                        </div>
                        <h3 className="text-lg font-bold text-[var(--color-text-main)] group-hover:text-[var(--color-primary)] transition-colors text-center">
                            {brand.name}
                        </h3>
                        <p className="text-xs text-[var(--color-text-muted)] mt-1 uppercase tracking-wider font-semibold">
                            {brand.type === 'car' ? '' : brand.type}
                        </p>
                    </Link>
                ))}
            </div>

            {filteredBrands.length === 0 && (
                <div className="text-center py-20">
                    <p className="text-xl text-[var(--color-text-muted)]">No brands found matching "{search}"</p>
                    <button
                        onClick={() => { setSearch(''); setFilter('all'); }}
                        className="mt-4 text-[var(--color-primary)] hover:underline"
                    >
                        Clear filters
                    </button>
                </div>
            )}
        </div>
    );
}
